import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const LOGO_ICON_URL = "https://customer-assets.emergentagent.com/job_maternal-wellness-9/artifacts/f1y8jte1_Screenshot%202026-03-09%20at%2011.47.59%E2%80%AFPM.png";

// Navigation links matching the Navbar
const navLinks = [
  { name: "Home", path: "/" },
  { name: "Our North Star", path: "/north-star" },
  { name: "About the Initiative", path: "/about" },
  { name: "Framework for Action", path: "/framework" },
  { name: "Our Commitment", path: "/impact" },
  { name: "Join the Initiative", path: "/join" },
];

// Four Engines of Change
const enginesOfChange = [
  { name: "Impact", path: "/framework#impact" },
  { name: "Innovation", path: "/framework#innovation" },
  { name: "Investment", path: "/framework#investment" },
  { name: "Insight & Influence", path: "/framework#insight" },
];

export default function Footer() {
  return (
    <footer data-testid="footer" className="bg-[#0A1628] text-white relative overflow-hidden">
      {/* Subtle gradient accent line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#E8A33D]/30 to-transparent" />
      
      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-16">
          {/* Logo and Description */}
          <div className="md:col-span-1">
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
              className="inline-block mb-6"
            >
              <img 
                src={LOGO_ICON_URL} 
                alt="Healthy Birth Initiative" 
                className="h-14 w-14 object-cover rounded-full"
              />
            </motion.div>
            <p className="text-white font-medium text-sm mb-2">Healthy Birth Initiative</p>
            <p className="text-white/40 text-sm leading-relaxed">
              Accelerating reduction of low birthweight and improved outcomes for small and sick newborns.
            </p>
          </div>
          
          {/* Navigate - Updated to match Navbar */}
          <div>
            <h4 className="text-[11px] font-semibold tracking-[0.2em] uppercase text-white/30 mb-6">Navigate</h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                    className="text-white/50 hover:text-white text-sm transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:translate-x-1 inline-block"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Framework for Action */}
          <div>
            <h4 className="text-[11px] font-semibold tracking-[0.2em] uppercase text-white/30 mb-6">Framework for Action</h4>
            <ul className="space-y-3">
              {enginesOfChange.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.path}
                    className="text-white/50 hover:text-white text-sm transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:translate-x-1 inline-block"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Initiative */}
          <div>
            <h4 className="text-[11px] font-semibold tracking-[0.2em] uppercase text-white/30 mb-6">Initiative</h4>
            <div className="space-y-4 text-sm text-white/50">
              <p>Hosted by <a href="https://www.jhpiego.org" target="_blank" rel="noopener noreferrer" className="text-[#E8A33D] hover:text-[#E8A33D]/80 transition-colors duration-500">Jhpiego</a></p>
              <p>Affiliated with Johns Hopkins University</p>
              <Link 
                to="/join" 
                className="inline-flex items-center gap-2 text-[#E8A33D] hover:text-[#E8A33D]/80 transition-all duration-500 group"
              >
                Get In Touch 
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-500" />
              </Link>
            </div>
          </div>
        </div>
        
        {/* Bottom bar */}
        <div className="border-t border-white/10 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <img 
              src={LOGO_ICON_URL} 
              alt="HBI" 
              className="h-8 w-8 object-cover rounded-full opacity-50"
            />
            <p className="text-white/30 text-xs">
              © {new Date().getFullYear()} The Healthy Birth Initiative. All rights reserved.
            </p>
          </div>
          <p className="text-white/20 text-xs tracking-wide">
            Every birth should be a healthy birth.
          </p>
        </div>
      </div>
    </footer>
  );
}
